import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import Header from '@/components/Header';
import { useTeam } from '@/contexts/TeamContext';
import {
  Gear, UserPlus, UserMinus, SpinnerGap, CheckCircle,
  Crown, ShieldCheck, User, Clock, X, PaperPlaneRight, Envelope,
  PencilSimple, Trash, WarningCircle, ArrowRight, Bell, BellSlash,
  ArrowsClockwise, CalendarBlank
} from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';

const API = `${window.location.origin}/api`;

const ROLE_BADGE = {
  owner: { label: 'Owner', icon: Crown, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  admin: { label: 'Admin', icon: ShieldCheck, cls: 'bg-blue-50 text-navy border-blue-200' },
  member: { label: 'Member', icon: User, cls: 'bg-slate-50 text-slate-600 border-slate-200' },
};

const TABS = [
  { key: 'members', label: 'Members' },
  { key: 'workspace', label: 'Workspace' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'danger', label: 'Danger Zone' },
];

function TeamSettings({ user }) {
  const navigate = useNavigate();
  const { activeTeam, fetchTeams, switchTeam } = useTeam();
  const [tab, setTab] = useState('members');
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Invite form
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');

  // Workspace tab
  const [editingName, setEditingName] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Transfer ownership
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferTarget, setTransferTarget] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Delete team
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Notification prefs
  const [prefs, setPrefs] = useState(null);
  const [loadingPrefs, setLoadingPrefs] = useState(false);

  const teamId = activeTeam?.team_id;
  const myRole = activeTeam?.my_role;
  const isOwner = myRole === 'owner';
  const canInvite = isOwner || myRole === 'admin';

  const fetchData = useCallback(async () => {
    if (!teamId) return;
    try {
      const [mRes, iRes] = await Promise.all([
        axios.get(`${API}/teams/${teamId}/members`, { withCredentials: true }),
        canInvite ? axios.get(`${API}/teams/${teamId}/invites`, { withCredentials: true }) : Promise.resolve({ data: [] }),
      ]);
      setMembers(mRes.data);
      setInvites(iRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [teamId, canInvite]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (activeTeam) setTeamName(activeTeam.team_name); }, [activeTeam]);

  const fetchNotifPrefs = useCallback(async () => {
    if (!teamId) return;
    setLoadingPrefs(true);
    try {
      const res = await axios.get(`${API}/teams/${teamId}/notification-prefs`, { withCredentials: true });
      setPrefs(res.data);
    } catch { setPrefs({ task_assigned: true, task_approved: true, task_rejected: true, member_joined: true, comment_on_task: true }); }
    finally { setLoadingPrefs(false); }
  }, [teamId]);

  useEffect(() => { if (tab === 'notifications') fetchNotifPrefs(); }, [tab, fetchNotifPrefs]);

  if (!activeTeam) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header user={user} />
        <main className="max-w-2xl mx-auto p-8 text-center pt-16">
          <p className="text-slate-500">Switch to a team workspace to manage settings.</p>
        </main>
      </div>
    );
  }

  // --- Members tab actions ---
  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteSuccess('');
    try {
      await axios.post(`${API}/teams/${teamId}/invites`, { email: inviteEmail.trim(), role: inviteRole }, { withCredentials: true });
      setInviteSuccess(inviteEmail.trim());
      setInviteEmail('');
      setInviteRole('member');
      setTimeout(() => { setInviteSuccess(''); setShowInviteForm(false); }, 3000);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send invite');
    } finally { setInviting(false); }
  };

  const cancelInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/teams/${teamId}/invites/${inviteId}`, { withCredentials: true });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const resendInvite = async (inviteId) => {
    try {
      await axios.post(`${API}/teams/${teamId}/invites/${inviteId}/resend`, {}, { withCredentials: true });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to resend'); }
  };

  const changeRole = async (memberId, newRole) => {
    try {
      await axios.put(`${API}/teams/${teamId}/members/${memberId}/role`, { role: newRole }, { withCredentials: true });
      fetchData();
      fetchTeams();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const deactivate = async (memberId) => {
    if (!window.confirm('Deactivate this member? Their tasks will become unassigned.')) return;
    try {
      await axios.put(`${API}/teams/${teamId}/members/${memberId}/deactivate`, {}, { withCredentials: true });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  // --- Workspace tab actions ---
  const saveName = async () => {
    if (!teamName.trim() || teamName.trim() === activeTeam.team_name) { setEditingName(false); return; }
    setSavingName(true);
    try {
      await axios.put(`${API}/teams/${teamId}`, { team_name: teamName.trim() }, { withCredentials: true });
      await fetchTeams();
      setEditingName(false);
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setSavingName(false); }
  };

  const handleTransfer = async () => {
    if (!transferTarget) return;
    setTransferring(true);
    try {
      await axios.post(`${API}/teams/${teamId}/transfer-ownership`, { new_owner_user_id: transferTarget }, { withCredentials: true });
      await fetchTeams();
      fetchData();
      setShowTransfer(false);
      setTransferTarget('');
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setTransferring(false); }
  };

  const handleDelete = async () => {
    if (deleteConfirm !== activeTeam.team_name) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/teams/${teamId}`, { withCredentials: true });
      switchTeam(null);
      await fetchTeams();
      navigate('/dashboard');
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setDeleting(false); }
  };

  // --- Notification pref toggle ---
  const togglePref = async (key) => {
    if (!prefs) return;
    const newVal = !prefs[key];
    setPrefs(p => ({ ...p, [key]: newVal }));
    try {
      await axios.put(`${API}/teams/${teamId}/notification-prefs`, { [key]: newVal }, { withCredentials: true });
    } catch { setPrefs(p => ({ ...p, [key]: !newVal })); }
  };

  const activeMembers = members.filter(m => m.status === 'active');
  const deactivatedMembers = members.filter(m => m.status === 'deactivated');
  const ownerMember = activeMembers.find(m => m.role === 'owner');

  const formatDate = (d) => {
    if (!d) return '';
    try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return ''; }
  };

  // Filter tabs: only show danger zone to owner
  const visibleTabs = isOwner ? TABS : TABS.filter(t => t.key !== 'danger' && t.key !== 'workspace');

  return (
    <div className="min-h-screen bg-slate-50">
      <Header user={user} />
      <main className="max-w-3xl mx-auto p-6 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-slate-900 mb-1 flex items-center gap-3">
              <Gear size={28} weight="duotone" className="text-navy" />
              Team Settings
            </h1>
            <p className="text-sm text-slate-500">{activeTeam.team_name}</p>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="flex gap-1 mb-6 bg-white border border-slate-200 rounded-lg p-1" data-testid="settings-tabs">
          {visibleTabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                tab === t.key
                  ? t.key === 'danger' ? 'bg-red-50 text-red-700' : 'bg-navy text-white'
                  : t.key === 'danger' ? 'text-red-500 hover:bg-red-50' : 'text-slate-500 hover:text-navy hover:bg-slate-50'
              }`}
              data-testid={`tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading && tab === 'members' ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-4 border-navy border-t-transparent" /></div>
        ) : (
          <>
            {/* =================== MEMBERS TAB =================== */}
            {tab === 'members' && (
              <div>
                {/* Invite button */}
                {canInvite && !showInviteForm && (
                  <button onClick={() => setShowInviteForm(true)}
                    className="mb-4 bg-navy text-white hover:bg-navy-hover transition-all shadow-sm hover:shadow-md font-medium px-4 py-2.5 rounded-lg flex items-center gap-2 text-sm"
                    data-testid="invite-member-btn">
                    <UserPlus size={18} weight="bold" /> Invite Member
                  </button>
                )}

                {/* Invite form */}
                <AnimatePresence>
                  {showInviteForm && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-4 overflow-hidden">
                      {inviteSuccess ? (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-5 py-4 flex items-center gap-3" data-testid="invite-success">
                          <CheckCircle size={20} weight="fill" className="text-emerald-600" />
                          <div>
                            <p className="text-sm font-medium text-emerald-800">Invite sent to {inviteSuccess}</p>
                            <p className="text-xs text-emerald-600">They'll receive an email with a join link.</p>
                          </div>
                        </div>
                      ) : (
                        <form onSubmit={handleInvite} className="bg-white border border-blue-200 rounded-lg p-5" data-testid="invite-form">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-heading font-medium text-slate-900 flex items-center gap-2">
                              <Envelope size={16} weight="duotone" className="text-navy" /> Invite a team member
                            </h3>
                            <button type="button" onClick={() => setShowInviteForm(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
                          </div>
                          <div className="flex flex-wrap items-end gap-3">
                            <div className="flex-1 min-w-[220px]">
                              <label className="block text-xs font-medium text-slate-600 mb-1.5">Email address</label>
                              <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required autoFocus
                                placeholder="ngozi@example.com"
                                className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 w-full text-sm text-slate-900 focus:border-navy focus:ring-2 focus:ring-blue-100"
                                data-testid="invite-email-input" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1.5">Role</label>
                              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
                                className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700" data-testid="invite-role-select">
                                <option value="member">Member</option>
                                <option value="admin">Admin</option>
                              </select>
                            </div>
                            <div className="flex gap-2">
                              <button type="submit" disabled={inviting || !inviteEmail.trim()}
                                className="bg-navy text-white hover:bg-navy-hover disabled:opacity-50 font-medium px-4 py-2.5 rounded-lg text-sm flex items-center gap-1.5"
                                data-testid="send-invite-btn">
                                {inviting ? <SpinnerGap size={14} className="animate-spin" /> : <PaperPlaneRight size={14} weight="fill" />}
                                {inviting ? 'Sending...' : 'Send Invite'}
                              </button>
                              <button type="button" onClick={() => setShowInviteForm(false)}
                                className="text-slate-500 border border-slate-200 hover:bg-slate-50 font-medium px-3 py-2.5 rounded-lg text-sm">Cancel</button>
                            </div>
                          </div>
                        </form>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="space-y-3">
                  {/* Pending Invites */}
                  {invites.length > 0 && (
                    <div className="mb-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Pending Invites</p>
                      {invites.map(inv => (
                        <div key={inv.invite_id} className="bg-amber-50/50 border border-amber-200/60 rounded-lg p-3.5 flex items-center gap-3 mb-2" data-testid={`invite-${inv.invite_id}`}>
                          <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <Clock size={16} className="text-amber-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700">{inv.invited_email}</p>
                            <p className="text-xs text-slate-400">
                              Invited as <span className="font-medium">{inv.role}</span> · Expires {new Date(inv.expires_at).toLocaleDateString()}
                            </p>
                          </div>
                          <span className="rounded-md px-2 py-0.5 text-[11px] font-medium border bg-amber-50 text-amber-700 border-amber-200">Pending</span>
                          {canInvite && (
                            <div className="flex items-center gap-1">
                              <button onClick={() => resendInvite(inv.invite_id)}
                                className="text-slate-400 hover:text-navy transition-colors p-1" title="Resend invite"
                                data-testid={`resend-invite-${inv.invite_id}`}>
                                <ArrowsClockwise size={14} />
                              </button>
                              <button onClick={() => cancelInvite(inv.invite_id)}
                                className="text-slate-300 hover:text-red-500 transition-colors p-1" title="Cancel invite"
                                data-testid={`cancel-invite-${inv.invite_id}`}>
                                <X size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Active Members */}
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Active Members ({activeMembers.length})</p>
                  {activeMembers.map(m => {
                    const roleInfo = ROLE_BADGE[m.role] || ROLE_BADGE.member;
                    const RoleIcon = roleInfo.icon;
                    const isMe = m.user_id === user?.user_id;
                    return (
                      <div key={m.user_id} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3" data-testid={`member-${m.user_id}`}>
                        {m.picture ? <img src={m.picture} alt="" className="w-9 h-9 rounded-full border border-slate-200" /> :
                          <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><User size={16} className="text-slate-400" /></div>}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{m.name}{isMe && ' (you)'}</p>
                          <p className="text-xs text-slate-400">{m.email} · Joined {formatDate(m.joined_at)}</p>
                        </div>
                        <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium border flex items-center gap-1 ${roleInfo.cls}`}>
                          <RoleIcon size={11} weight="fill" /> {roleInfo.label}
                        </span>
                        {isOwner && !isMe && (
                          <div className="flex items-center gap-1">
                            <select value={m.role} onChange={(e) => changeRole(m.user_id, e.target.value)}
                              className="text-xs border border-slate-200 rounded-md px-1.5 py-1 text-slate-600" data-testid={`role-select-${m.user_id}`}>
                              <option value="admin">Admin</option>
                              <option value="member">Member</option>
                            </select>
                            <button onClick={() => deactivate(m.user_id)} className="text-slate-300 hover:text-red-500 transition-colors p-1"
                              title="Deactivate member" data-testid={`deactivate-${m.user_id}`}><UserMinus size={14} /></button>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Deactivated */}
                  {deactivatedMembers.length > 0 && (
                    <>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mt-6 mb-2">Deactivated</p>
                      {deactivatedMembers.map(m => (
                        <div key={m.user_id} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3 opacity-50" data-testid={`member-${m.user_id}`}>
                          <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><User size={16} className="text-slate-400" /></div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{m.name}</p>
                            <p className="text-xs text-slate-400">{m.email}</p>
                          </div>
                          <span className="rounded-md px-2 py-0.5 text-[11px] font-medium border bg-red-50 text-red-500 border-red-200">Deactivated</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* =================== WORKSPACE TAB =================== */}
            {tab === 'workspace' && isOwner && (
              <div className="space-y-6">
                {/* Team name */}
                <div className="bg-white border border-slate-200 rounded-lg p-5">
                  <h3 className="text-sm font-heading font-semibold text-slate-900 mb-4">Workspace Details</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1.5">Team Name</label>
                      {editingName ? (
                        <div className="flex gap-2">
                          <input type="text" value={teamName} onChange={(e) => setTeamName(e.target.value)}
                            className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-navy focus:ring-2 focus:ring-blue-100"
                            autoFocus data-testid="edit-team-name-input" />
                          <button onClick={saveName} disabled={savingName}
                            className="bg-navy text-white px-3 py-2 rounded-lg text-sm font-medium" data-testid="save-team-name-btn">
                            {savingName ? <SpinnerGap size={14} className="animate-spin" /> : 'Save'}
                          </button>
                          <button onClick={() => { setEditingName(false); setTeamName(activeTeam.team_name); }}
                            className="text-slate-400 px-2">Cancel</button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-900 font-medium">{activeTeam.team_name}</span>
                          <button onClick={() => setEditingName(true)}
                            className="text-xs text-navy font-medium hover:underline flex items-center gap-1" data-testid="edit-name-btn">
                            <PencilSimple size={12} /> Edit
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Created By</label>
                        <p className="text-sm text-slate-700">{ownerMember?.name || 'Unknown'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Created</label>
                        <p className="text-sm text-slate-700">{formatDate(activeTeam.created_at)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Transfer Ownership */}
                <div className="bg-white border border-slate-200 rounded-lg p-5">
                  <h3 className="text-sm font-heading font-semibold text-slate-900 mb-2">Transfer Ownership</h3>
                  <p className="text-xs text-slate-500 mb-4">Transfer the owner role to another active member. You will become an Admin.</p>
                  {!showTransfer ? (
                    <button onClick={() => setShowTransfer(true)}
                      className="text-sm text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 font-medium px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                      data-testid="transfer-ownership-btn">
                      <ArrowRight size={14} weight="bold" /> Transfer Ownership
                    </button>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-sm text-amber-800 mb-3 font-medium">Select the new owner:</p>
                      <select value={transferTarget} onChange={(e) => setTransferTarget(e.target.value)}
                        className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm mb-3 bg-white" data-testid="transfer-target-select">
                        <option value="">Choose a member...</option>
                        {activeMembers.filter(m => m.user_id !== user?.user_id).map(m => (
                          <option key={m.user_id} value={m.user_id}>{m.name} ({m.email})</option>
                        ))}
                      </select>
                      <div className="flex gap-2">
                        <button onClick={handleTransfer} disabled={!transferTarget || transferring}
                          className="bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"
                          data-testid="confirm-transfer-btn">
                          {transferring ? <SpinnerGap size={14} className="animate-spin" /> : <ArrowRight size={14} weight="bold" />}
                          Confirm Transfer
                        </button>
                        <button onClick={() => { setShowTransfer(false); setTransferTarget(''); }}
                          className="text-sm text-slate-500 hover:text-slate-700">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* =================== NOTIFICATIONS TAB =================== */}
            {tab === 'notifications' && (
              <div className="bg-white border border-slate-200 rounded-lg p-5">
                <h3 className="text-sm font-heading font-semibold text-slate-900 mb-1">Notification Preferences</h3>
                <p className="text-xs text-slate-500 mb-5">Choose what notifications you'd like to receive for this team.</p>

                {loadingPrefs ? (
                  <div className="flex justify-center py-6"><SpinnerGap size={20} className="text-navy animate-spin" /></div>
                ) : prefs ? (
                  <div className="space-y-3">
                    {[
                      { key: 'task_assigned', label: 'Task assigned to me', desc: 'When a task is assigned to you by a team member' },
                      { key: 'task_approved', label: 'My task approved', desc: 'When a task you submitted for approval is approved' },
                      { key: 'task_rejected', label: 'My task rejected', desc: 'When a task you submitted for approval is rejected' },
                      { key: 'member_joined', label: 'New member joined', desc: 'When a new member joins the team' },
                      { key: 'comment_on_task', label: 'Comment on my task', desc: 'When someone comments on a task assigned to you' },
                    ].map(item => (
                      <div key={item.key} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                        <div className="flex items-center gap-3">
                          {prefs[item.key] ? <Bell size={16} className="text-navy" /> : <BellSlash size={16} className="text-slate-300" />}
                          <div>
                            <p className="text-sm font-medium text-slate-800">{item.label}</p>
                            <p className="text-xs text-slate-400">{item.desc}</p>
                          </div>
                        </div>
                        <button onClick={() => togglePref(item.key)}
                          className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${prefs[item.key] ? 'bg-navy' : 'bg-slate-200'}`}
                          data-testid={`toggle-${item.key}`}>
                          <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${prefs[item.key] ? 'left-[22px]' : 'left-0.5'}`} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            {/* =================== DANGER ZONE TAB =================== */}
            {tab === 'danger' && isOwner && (
              <div className="space-y-4">
                <div className="bg-white border-2 border-red-200 rounded-lg p-5">
                  <h3 className="text-sm font-heading font-semibold text-red-700 mb-2 flex items-center gap-2">
                    <WarningCircle size={16} weight="fill" /> Danger Zone
                  </h3>
                  <p className="text-xs text-slate-500 mb-6">These actions are destructive and cannot be undone.</p>

                  {/* Delete team */}
                  <div className="border-t border-red-100 pt-4">
                    <h4 className="text-sm font-medium text-slate-900 mb-1">Delete Team</h4>
                    <p className="text-xs text-slate-500 mb-3">Permanently delete this team, all shared matters, tasks, and member access.</p>
                    {!showDelete ? (
                      <button onClick={() => setShowDelete(true)}
                        className="bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors"
                        data-testid="delete-team-btn">
                        <Trash size={14} weight="bold" /> Delete Team
                      </button>
                    ) : (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-sm text-red-800 mb-2 font-medium">
                          Are you sure you want to delete {activeTeam.team_name}?
                        </p>
                        <p className="text-xs text-red-600 mb-3">
                          This will permanently remove the team, all shared matters, tasks, and member access. This action cannot be undone.
                        </p>
                        <p className="text-xs text-slate-600 mb-2">
                          Type <strong>{activeTeam.team_name}</strong> to confirm:
                        </p>
                        <input type="text" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)}
                          placeholder={activeTeam.team_name}
                          className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm mb-3 focus:ring-2 focus:ring-red-100 focus:border-red-400"
                          data-testid="delete-confirm-input" />
                        <div className="flex gap-2">
                          <button onClick={handleDelete} disabled={deleteConfirm !== activeTeam.team_name || deleting}
                            className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 transition-colors"
                            data-testid="confirm-delete-btn">
                            {deleting ? <SpinnerGap size={14} className="animate-spin" /> : <Trash size={14} weight="bold" />}
                            Permanently Delete
                          </button>
                          <button onClick={() => { setShowDelete(false); setDeleteConfirm(''); }}
                            className="text-sm text-slate-500 hover:text-slate-700">Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default TeamSettings;
